import * as React from 'react';

import {
  Box,
  Flex,
  Icon,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  useColorModeValue,
  type TabListProps,
  type TabPanelProps,
  type TabProps,
  type TabsProps,
} from '@chakra-ui/react';
import { LoadingSpinner } from '@saas-ui/react';

// Custom Tab Component with enhanced styling
export interface CustomTabProps extends Omit<TabProps, 'isDisabled'> {
  icon?: React.ElementType;
  isLoading?: boolean;
  badge?: string | number;
  isDisabled?: boolean;
  isSelected?: boolean;
}

const CustomTab = React.forwardRef<HTMLButtonElement, CustomTabProps>(
  (
    { children, icon, badge, isDisabled, isSelected, isLoading, ...props },
    ref
  ) => {
    const tabColor = useColorModeValue('gray.600', 'tabs.font');
    const tabHoverColor = useColorModeValue('gray.800', 'tabs.font-hover');
    const tabActiveColor = useColorModeValue(
      'primary.600',
      'tabs.font-selected'
    );
    const tabDisabledColor = useColorModeValue(
      'gray.200',
      'tabs.font-disabled'
    );

    const badgeBg = useColorModeValue('primary.100', 'tabs.counter-bg');
    const badgeBgSelected = useColorModeValue(
      'primary.300',
      'tabs.counter-bg-selected'
    );
    const badgeBgDisabled = useColorModeValue(
      'gray.200',
      'tabs.counter-bg-disabled'
    );
    const badgeColor = useColorModeValue('primary.800', 'tabs.counter');

    return (
      <Tab
        ref={ref}
        isDisabled={isDisabled}
        _selected={{
          color: tabActiveColor,
          borderBottom: '2px solid',
          borderBottomColor: tabActiveColor,
          '.badge': {
            bg: badgeBgSelected,
          },
        }}
        _hover={{
          color: tabHoverColor,
        }}
        _disabled={{
          color: tabDisabledColor,
          cursor: 'not-allowed',
        }}
        _active={{
          bg: 'transparent',
        }}
        color={tabColor}
        fontWeight='medium'
        px={4}
        py={2.5}
        minH={9}
        lineHeight='20px'
        mb='-1px'
        position='relative'
        whiteSpace='nowrap'
        className='group'
        {...props}
      >
        <Flex align='center' gap={2}>
          {icon && <Icon as={icon} boxSize={4} />}
          <Text fontWeight='semibold'>{children}</Text>
          {isLoading ? (
            <LoadingSpinner
              as='span'
              size='sm'
              verticalAlign='text-bottom'
              color='glass-items.icon-hover'
              borderBottomColor='glass-items.border-hover'
              borderLeftColor='glass-items.border-hover'
            />
          ) : badge ? (
            <Box
              bg={badgeBg}
              color={badgeColor}
              borderRadius='full'
              fontSize='11px'
              lineHeight={1}
              fontWeight='medium'
              minW='18px'
              h='18px'
              px={1}
              display='flex'
              alignItems='center'
              justifyContent='center'
              _groupDisabled={{
                bg: badgeBgDisabled,
              }}
              sx={{
                bg: isSelected ? badgeBgSelected : badgeBg,
              }}
              className='badge'
            >
              {badge}
            </Box>
          ) : null}
        </Flex>
      </Tab>
    );
  }
);

CustomTab.displayName = 'CustomTab';

// Enhanced TabList Component
export interface CustomTabListProps extends TabListProps {
  variant?: 'line' | 'enclosed' | 'soft-rounded' | 'solid-rounded';
  size?: 'sm' | 'md' | 'lg';
}

const CustomTabList = React.forwardRef<HTMLDivElement, CustomTabListProps>(
  ({ variant = 'line', size = 'md', children, ...props }, ref) => {
    // Move hooks to component level
    const borderColor = useColorModeValue('gray.200', 'border.light');
    const bgLight = useColorModeValue('gray.50', 'app-background');
    const bgSoft = useColorModeValue('gray.100', 'app-background');
    const bgSolid = useColorModeValue('gray.200', 'app-background');

    const getVariantStyles = () => {
      switch (variant) {
        case 'enclosed':
          return {
            border: '1px solid',
            borderColor,
            borderRadius: 'md',
            bg: bgLight,
          };
        case 'soft-rounded':
          return {
            bg: bgSoft,
            borderRadius: 'full',
            p: 1,
          };
        case 'solid-rounded':
          return {
            bg: bgSolid,
            borderRadius: 'full',
            p: 1,
          };
        default:
          return {};
      }
    };

    const getSizeStyles = () => {
      switch (size) {
        case 'sm':
          return { fontSize: 'sm', pt: 1 };
        case 'lg':
          return { fontSize: 'lg', pt: 2 };
        default:
          return { fontSize: 'md', pt: 1 };
      }
    };

    return (
      <TabList
        ref={ref}
        overflow={{ base: 'hidden', md: 'visible' }}
        overflowX={{ base: 'auto', md: 'visible' }}
        {...getVariantStyles()}
        {...getSizeStyles()}
        {...props}
      >
        {children}
      </TabList>
    );
  }
);

CustomTabList.displayName = 'CustomTabList';

// Enhanced TabPanel Component
export interface CustomTabPanelProps extends TabPanelProps {
  isLoading?: boolean;
}

const CustomTabPanel = React.forwardRef<HTMLDivElement, CustomTabPanelProps>(
  ({ children, isLoading, ...props }, ref) => {
    if (isLoading) {
      return (
        <TabPanel tabIndex={-1} ref={ref} py={4} px={0} {...props}>
          <Flex align='center' justify='center' minH='200px'>
            <Text color='gray.500'>Loading...</Text>
          </Flex>
        </TabPanel>
      );
    }

    return (
      <TabPanel tabIndex={-1} ref={ref} py={4} px={0} {...props}>
        {children}
      </TabPanel>
    );
  }
);

CustomTabPanel.displayName = 'CustomTabPanel';

// Main Tab Container Component
export interface CustomTabsProps extends TabsProps {
  variant?: 'line' | 'enclosed' | 'soft-rounded' | 'solid-rounded';
  size?: 'sm' | 'md' | 'lg';
  orientation?: 'horizontal' | 'vertical';
}

const CustomTabs = React.forwardRef<HTMLDivElement, CustomTabsProps>(
  (
    {
      variant = 'line',
      size = 'md',
      orientation = 'horizontal',
      children,
      ...props
    },
    ref
  ) => {
    return (
      <Tabs
        ref={ref}
        variant={variant}
        size={size}
        orientation={orientation}
        colorScheme='primary'
        {...props}
      >
        {children}
      </Tabs>
    );
  }
);

CustomTabs.displayName = 'CustomTabs';

// Export all components
export {
  CustomTabs as Tabs,
  CustomTabList as TabList,
  CustomTab as Tab,
  TabPanels,
  CustomTabPanel as TabPanel,
  CustomTab as TabButton, // Alias for clarity
};

// Export types
export type {
  CustomTabsProps as TabsProps,
  CustomTabListProps as TabListProps,
  CustomTabProps as TabProps,
  CustomTabPanelProps as TabPanelProps,
};
